import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const QuickFilters = ({ onFilterChange, activeFilters = {} }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const filterOptions = {
    status: [
      { value: 'open', label: 'Open', icon: 'AlertCircle', color: 'text-error' },
      { value: 'in-progress', label: 'In Progress', icon: 'Clock', color: 'text-warning' },
      { value: 'investigating', label: 'Investigating', icon: 'Search', color: 'text-accent' },
      { value: 'resolved', label: 'Resolved', icon: 'CheckCircle', color: 'text-success' }
    ],
    severity: [
      { value: 'critical', label: 'Critical', icon: 'AlertTriangle', color: 'text-error' },
      { value: 'high', label: 'High', icon: 'AlertCircle', color: 'text-warning' },
      { value: 'medium', label: 'Medium', icon: 'Info', color: 'text-accent' },
      { value: 'low', label: 'Low', icon: 'Minus', color: 'text-success' }
    ],
    assignment: [
      { value: 'assigned-to-me', label: 'Assigned to Me', icon: 'User', color: 'text-primary' },
      { value: 'unassigned', label: 'Unassigned', icon: 'UserX', color: 'text-muted-foreground' },
      { value: 'my-team', label: 'My Team', icon: 'Users', color: 'text-secondary' }
    ]
  };

  const quickActions = [
    {
      label: 'Create Incident',
      icon: 'Plus',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      action: () => navigate('/create-incident')
    },
    {
      label: 'View Reports',
      icon: 'BarChart3',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      action: () => navigate('/reports-analytics')
    },
    {
      label: 'System Settings',
      icon: 'Settings',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      action: () => navigate('/system-settings')
    }
  ];

  const handleFilterClick = (filterType, filterValue) => {
    const newFilters = { ...activeFilters };
    
    if (newFilters?.[filterType] === filterValue) {
      delete newFilters?.[filterType];
    } else {
      newFilters[filterType] = filterValue;
    }
    
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    onFilterChange({});
  };

  const getActiveFilterCount = () => {
    return Object.keys(activeFilters)?.length;
  };

  const isFilterActive = (filterType, filterValue) => {
    return activeFilters?.[filterType] === filterValue;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-light mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-foreground">Quick Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {getActiveFilterCount() > 0 && (
            <button
              onClick={handleClearFilters}
              className="flex items-center space-x-1 px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              <Icon name="X" size={14} />
              <span>Clear</span>
            </button>
          )}
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="md:hidden flex items-center space-x-1 px-3 py-1 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-colors duration-200"
          >
            <span>{isExpanded ? 'Less' : 'More'}</span>
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
          </button>
        </div>
      </div>
      <div className={`space-y-6 ${isExpanded ? 'block' : 'hidden md:block'}`}>
        {/* Status Filters */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Status</h4>
          <div className="flex flex-wrap gap-2">
            {filterOptions?.status?.map((option) => (
              <button
                key={option?.value}
                onClick={() => handleFilterClick('status', option?.value)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  isFilterActive('status', option?.value)
                    ? 'bg-primary text-primary-foreground border-primary' :'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                <Icon 
                  name={option?.icon} 
                  size={14} 
                  className={isFilterActive('status', option?.value) ? '' : option?.color} 
                />
                <span>{option?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Severity Filters */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Severity</h4>
          <div className="flex flex-wrap gap-2">
            {filterOptions?.severity?.map((option) => (
              <button
                key={option?.value}
                onClick={() => handleFilterClick('severity', option?.value)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  isFilterActive('severity', option?.value)
                    ? 'bg-primary text-primary-foreground border-primary' :'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                <Icon 
                  name={option?.icon} 
                  size={14} 
                  className={isFilterActive('severity', option?.value) ? '' : option?.color} 
                />
                <span>{option?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Assignment Filters */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Assignment</h4>
          <div className="flex flex-wrap gap-2">
            {filterOptions?.assignment?.map((option) => (
              <button
                key={option?.value}
                onClick={() => handleFilterClick('assignment', option?.value)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ${
                  isFilterActive('assignment', option?.value)
                    ? 'bg-primary text-primary-foreground border-primary' :'bg-background text-foreground border-border hover:bg-muted'
                }`}
              >
                <Icon 
                  name={option?.icon} 
                  size={14} 
                  className={isFilterActive('assignment', option?.value) ? '' : option?.color} 
                />
                <span>{option?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="text-sm font-medium text-muted-foreground mb-3">Quick Actions</h4>
        <div className="flex flex-wrap gap-2">
          {quickActions?.map((action, index) => (
            <button
              key={index}
              onClick={action?.action}
              className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg ${action?.bgColor} ${action?.color} hover:scale-105 transition-all duration-200`}
            >
              <Icon name={action?.icon} size={14} />
              <span>{action?.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuickFilters;